import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRepositories, useSyncRepository, useRegisterWebhook, useUnregisterWebhook } from '@/hooks/useRepositories';
import { useGitLabStatus, useSyncRepositories, useFullSync } from '@/hooks/useGitLab';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  GitBranch,
  RefreshCw,
  Webhook,
  Search,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';
import { Repository } from '@/types';

export default function Repositories() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  // Queries
  // Syncing and the GitLab connection are for the CEO and the PM; everyone else sees their repos
  const { user } = useAuth();
  const isLead = user?.role === 'executive' || user?.role === 'pm';
  const { data: gitlabStatus, isLoading: statusLoading } = useGitLabStatus(isLead);
  const { data: reposData, isLoading: reposLoading, refetch: refetchRepos } = useRepositories({
    page,
    limit: 20,
    search: searchQuery || undefined,
    active: true,
  });

  // Mutations
  const syncReposMutation = useSyncRepositories();
  const fullSyncMutation = useFullSync();
  const syncRepoMutation = useSyncRepository();
  const registerWebhookMutation = useRegisterWebhook();
  const unregisterWebhookMutation = useUnregisterWebhook();

  const handleSyncAll = async () => {
    await syncReposMutation.mutateAsync();
    refetchRepos();
  };

  const handleFullSync = async () => {
    await fullSyncMutation.mutateAsync({ commitLimit: 50 });
    refetchRepos();
  };

  const handleSyncRepo = async (repoId: string) => {
    await syncRepoMutation.mutateAsync({ id: repoId, commitLimit: 100 });
  };

  const handleToggleWebhook = async (repo: Repository) => {
    if (repo.webhookActive) {
      await unregisterWebhookMutation.mutateAsync(repo.id);
    } else {
      await registerWebhookMutation.mutateAsync(repo.id);
    }
  };

  const isSyncing = syncReposMutation.isPending || fullSyncMutation.isPending || gitlabStatus?.isSyncing;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Repositories</h1>
          <p className="text-muted-foreground">
            Manage GitLab repositories and webhooks
          </p>
        </div>
        {isLead && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSyncAll}
            disabled={isSyncing || !gitlabStatus?.connected}
          >
            {syncReposMutation.isPending ? (
              <LoadingSpinner className="h-4 w-4 mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync Repos
          </Button>
          <Button
            onClick={handleFullSync}
            disabled={isSyncing || !gitlabStatus?.connected}
          >
            {fullSyncMutation.isPending ? (
              <LoadingSpinner className="h-4 w-4 mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Full Sync
          </Button>
        </div>
        )}
      </div>

      {/* GitLab Status (CEO and PM) */}
      {isLead && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">GitLab Connection</CardTitle>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                {gitlabStatus?.connected ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <span>
                  {gitlabStatus?.connected ? 'Connected' : 'Disconnected'}
                  {gitlabStatus?.version && ` (v${gitlabStatus.version})`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <GitBranch className="h-4 w-4" />
                <span>{gitlabStatus?.repoCount || 0} repos</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  {gitlabStatus?.lastSyncTime
                    ? `Last sync: ${formatRelativeTime(gitlabStatus.lastSyncTime)}`
                    : 'Never synced'}
                </span>
              </div>
              {gitlabStatus?.currentUser && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>as @{gitlabStatus.currentUser.username}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search repositories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Repository List */}
      {reposLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !reposData?.data?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No repositories found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery
                ? 'Try a different search term'
                : 'Sync with GitLab to import repositories'}
            </p>
            {!searchQuery && gitlabStatus?.connected && (
              <Button onClick={handleSyncAll} disabled={isSyncing}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync from GitLab
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {reposData.data.map((repo) => (
            <Card key={repo.id} className="hover:border-primary/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <GitBranch className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <Link
                        to={`/repos/${repo.id}`}
                        className="font-medium hover:text-primary truncate"
                      >
                        {repo.name}
                      </Link>
                      {repo.gitlabUrl && (
                        <a
                          href={repo.gitlabUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {repo.gitlabPath}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <GitBranch className="h-3 w-3" />
                        {repo.defaultBranch}
                      </span>
                      {repo.lastCommitAt && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(repo.lastCommitAt)}
                        </span>
                      )}
                      {repo.webhookActive ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <Webhook className="h-3 w-3" />
                          Webhook active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Webhook className="h-3 w-3" />
                          No webhook
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSyncRepo(repo.id)}
                      disabled={syncRepoMutation.isPending}
                    >
                      {syncRepoMutation.isPending ? (
                        <LoadingSpinner className="h-4 w-4" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant={repo.webhookActive ? 'destructive' : 'outline'}
                      size="sm"
                      onClick={() => handleToggleWebhook(repo)}
                      disabled={
                        registerWebhookMutation.isPending ||
                        unregisterWebhookMutation.isPending
                      }
                    >
                      <Webhook className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {reposData?.pagination?.totalPages && reposData.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {reposData.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= reposData.pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
